import { useState } from 'react';
import { Popover } from '@material-ui/core';
import clsx from 'clsx';

import { PdvAngleUpIcon, PdvSearchIcon } from 'componentsV2/pdv';

import { useStyles } from './styles';

export interface AttributeOption {
  name: string;
  value?: string;
}

interface AttributePickerProps {
  options: AttributeOption[];
  value: string | null;
  /** show "name · value" (Wrong value) or just the name (Missing) */
  showValue: boolean;
  /** a badge on the row when the attribute already has a report */
  badgeFor: (name: string) => 'mine' | 'other' | null;
  emptyText: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (name: string) => void;
}

/**
 * The "Which attribute?" field: a select-looking button whose searchable list
 * floats over the dialog, so opening it never resizes the sheet.
 */
export const AttributePicker = ({
  options,
  value,
  showValue,
  badgeFor,
  emptyText,
  open,
  onOpenChange,
  onPick
}: AttributePickerProps) => {
  const classes = useStyles();
  // state, not a ref, so the list re-renders once its anchor exists
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);

  const filtered = options.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );
  const label = (o: AttributeOption) =>
    showValue ? `${o.name} · ${o.value ?? '—'}` : o.name;
  const picked = options.find(o => o.name === value);

  const setOpen = (next: boolean) => {
    setSearch('');
    setHighlight(0);
    onOpenChange(next);
  };
  const pick = (name: string) => {
    setSearch('');
    onPick(name);
  };

  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(classes.select, open && classes.selectOpen)}
        onClick={() => setOpen(!open)}>
        <span className={picked ? undefined : classes.placeholder}>
          {picked ? label(picked) : 'Choose an attribute'}
        </span>
        <PdvAngleUpIcon
          size={16}
          className={open ? undefined : classes.chevronClosed}
        />
      </button>

      <Popover
        open={open && Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.menu }}
        PaperProps={{
          role: 'listbox',
          // follows the field's width, which only the DOM knows
          style: { width: anchor?.offsetWidth }
        }}>
        <div className={classes.menuSearch}>
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label="Search attributes"
            placeholder="Search attributes"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') {
                setHighlight(h => Math.min(h + 1, filtered.length - 1));
                e.preventDefault();
              } else if (e.key === 'ArrowUp') {
                setHighlight(h => Math.max(h - 1, 0));
                e.preventDefault();
              } else if (e.key === 'Enter' && filtered[highlight]) {
                pick(filtered[highlight].name);
                e.preventDefault();
              }
            }}
          />
          <PdvSearchIcon />
        </div>
        <div className={classes.items}>
          {filtered.map((o, i) => {
            const badge = badgeFor(o.name);
            return (
              <button
                key={o.name}
                type="button"
                role="option"
                aria-selected={value === o.name}
                className={clsx(
                  classes.item,
                  i === highlight && classes.itemOn
                )}
                onClick={() => pick(o.name)}>
                <span>{label(o)}</span>
                {badge === 'mine' && (
                  <span className={clsx(classes.badge, classes.badgeMine)}>
                    You reported
                  </span>
                )}
                {badge === 'other' && (
                  <span className={classes.badge}>Reported</span>
                )}
              </button>
            );
          })}
          {!filtered.length && (
            <p className={classes.empty}>
              {options.length ? 'No matching attributes' : emptyText}
            </p>
          )}
        </div>
      </Popover>
    </>
  );
};
